import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useContext } from 'react';
import { FaTimes } from 'react-icons/fa';
import { Button, Input, Row } from 'reactstrap';
import InputLabel from '../../../../components/InputField/InputLabel';
import { WrapperField } from '../../../../components/InputField/WrapperField';
import { DialogContext } from '../../../../store/context/DialogContext';
import ButtonGroupNew from '../BusinessLiteForms/components/ButtonGroupNew';
import Entity from './components/EntityV2';
import { collectVisibleFieldErrors, CONTROL_PRONG_OWNERSHIP_MESSAGE, getFieldErrorValue, getVisibleWizardFieldOrder, validateDuplicateContacts } from './helperV2';
import { getIndividualShareholderOwnershipError } from '../helpers/ownershipValidation';
import User from './components/User';

const BTN = ({ onClick, label, disabled }) => {
    const btnStyle = {
        backgroundColor: '#fff',
        borderRadius: "10px",
        border: "1px solid #1678AE",
        color: '#1678AE',
        whiteSpace: 'normal',
        textAlign: 'left',
        maxWidth: '100%',
    };

    return (
        <div>
            <Button className='mb-3 mt-1' disabled={disabled} style={btnStyle} onClick={onClick}>
                {label}
            </Button>
        </div>
    );
};

const ADDRESS_LABELS = {
    REGISTERED: "Registered Address of Business",
    MAILING: "Mailing Address of Business",
};
const CONTROL_PERSON_BADGE_STYLE = {
    fontSize: 12,
    fontWeight: 600,
    background: '#E8F3FF',
    color: '#1678AE',
    border: '1px solid #B7D9F2',
    borderRadius: 999,
    padding: '2px 10px',
};

const buildLocalId = () => `local_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;

const buildMirroredAddressRow = (sourceAddress = {}, type, existingRow = null) => {
    const preservedIdentity = _.pick(existingRow || {}, ['_id', 'aEId', '_isNew']);
    return {
        ...sourceAddress,
        ...preservedIdentity,
        _localId: existingRow?._localId || buildLocalId(),
        type,
    };
};

const compactAddressField = (fieldMap, fieldKey) => {
    const jsonField = fieldMap?.[fieldKey];
    return jsonField ? { ...jsonField, noLabel: true } : jsonField;
};

const contextualStreetField = (fieldMap, fieldKey, contextLabel) => {
    const jsonField = fieldMap?.[fieldKey];
    if (!jsonField) return jsonField;

    return {
        ...jsonField,
        noLabel: true,
        required: true,
        label: contextLabel ? `Street Name for ${contextLabel}` : 'Street Name',
    };
};

const BusinessInformationPlus = ({
    asLabel,
    hideButtons,
    data,
    handleUpdate,
    hasError,
    setHasError,
    loading,
    cyclosFields,
    errors,
    setErrors,
    iPlusFields,
    wizard,
    handleChange,
    setData,
    handlePrev,
    applicationForm,
    maxAuthorizedUsers = 11,
    pageNum = 1,
    editMode = false,
}) => {
    const { showError } = useContext(DialogContext);
    // Broker-managed customers are restricted to a single authorized user.
    const effectiveMaxAuthorizedUsers = applicationForm?.brokerId ? 1 : maxAuthorizedUsers;

    const handleErrors = (field, errorMsg) => {
        setErrors((prevErrors) => {
            const currentValue = prevErrors?.[field];
            if (_.isEqual(currentValue, errorMsg)) {
                return prevErrors;
            }
            const updatedErrors = _.cloneDeep(prevErrors || {});
            updatedErrors[field] = errorMsg;
            return updatedErrors;
        });
    };

    const handleUpdateFormData = (updatedData) => {
        handleChange(updatedData);
    };

    const checkNext = () => {
        const visibleFieldOrder = getVisibleWizardFieldOrder({
            fields: iPlusFields,
            wizard,
            data,
            type: Number(applicationForm?.type),
        });
        const { fieldErrors } = collectVisibleFieldErrors({
            data,
            step: { wizard },
            iPlusFields,
            errors,
            type: Number(applicationForm?.type),
        });
        const mergedErrors = {
            ...(errors || {}),
            ...fieldErrors,
        };
        setErrors(mergedErrors);

        for (const fieldPath of visibleFieldOrder) {
            const fieldError = getFieldErrorValue(mergedErrors, fieldPath);
            if (fieldError) {
                setHasError(true);
                showError(fieldError);
                return false;
            }
        }

        const individuals = Array.isArray(data?.individuals) ? data.individuals : [];
        const entities = Array.isArray(data?.entities) ? data.entities : [];

        const duplicateContactErrors = validateDuplicateContacts({ ...data, individuals });
        const duplicateContactFields = Object.keys(duplicateContactErrors || {});
        if (duplicateContactFields.length) {
            setErrors((prevErrors) => ({
                ...(prevErrors || {}),
                ...duplicateContactErrors,
            }));
            setHasError(true);
            showError(duplicateContactErrors[duplicateContactFields[0]]);
            return false;
        }

        const individualOwnershipError = getIndividualShareholderOwnershipError(individuals);
        if (individualOwnershipError) {
            showError(individualOwnershipError);
            return false;
        }

        const hasControlProngOrQualifiedOwner = individuals.some((ind) => (
            ind?.isControlPerson === true
            || (ind?.isShareHolder === true && (parseFloat(ind?.percentOwner) || 0) >= 25)
        ));
        if (!hasControlProngOrQualifiedOwner) {
            showError(CONTROL_PRONG_OWNERSHIP_MESSAGE);
            return false;
        }

        const authorizedCount = individuals.filter((ind) => ind?.isAuthorizedUser).length;
        if (authorizedCount > effectiveMaxAuthorizedUsers) {
            showError(`Maximum ${effectiveMaxAuthorizedUsers} authorized user${effectiveMaxAuthorizedUsers === 1 ? "" : "s"} allowed. Currently: ${authorizedCount}`);
            return false;
        }

        for (let idx = 0; idx < individuals.length; idx += 1) {
            const person = individuals[idx];
            const userLabel = person?.isPrimaryUser ? 'Primary User' : `User ${idx + 1}`;
            const isShareHolder = person?.isShareHolder === true;
            const isAuthorizedUser = person?.isAuthorizedUser === true;
            const isIndependentDirector = person?.isIndependentDirector === true;
            const isControlPerson = person?.isControlPerson === true;
            const isDirector = person?.isDirector === true;

            if (!isShareHolder && !isAuthorizedUser && !isIndependentDirector && !isControlPerson && !isDirector) {
                showError(`${userLabel} should be either shareholder, authorized user, director, or Control Prong.`);
                return false;
            }

            if (isShareHolder) {
                if (person?.percentOwner === undefined || person?.percentOwner === null || person?.percentOwner === '') {
                    showError(`Please enter Percentage of Ownership for ${userLabel}`);
                    return false;
                }
            }

            if (isShareHolder) {
                if (!Array.isArray(person?.sourceOfWealthList) || !person?.sourceOfWealthList?.length) {
                    showError(`Please select at least one source of wealth for ${userLabel}`);
                    return false;
                }
            }

            if (isShareHolder) {
                const percentOwner = parseFloat(person?.percentOwner);
                if (Number.isNaN(percentOwner)) {
                    showError(`Please enter a valid percentage of ownership for ${userLabel}`);
                    return false;
                }
                if (percentOwner <= 0) {
                    showError(`Percent ownership must be greater than 0% for ${userLabel}`);
                    return false;
                }
                if (percentOwner > 100) {
                    showError(`Percent ownership cannot exceed 100% for ${userLabel}`);
                    return false;
                }
            }
        }

        setErrors({});
        return true;
    };

    const handleSubmit = () => {
        if (!checkNext()) {
            return;
        }

        setHasError(false);
        const nextStep = applicationForm?.getNextStepForForm?.({
            wizard,
            applicationForm,
            data,
        });
        return handleUpdate({ nextStep });
    };

    const handlePrincipalAddressWrapperChange = (nextData) => {
        handleUpdateFormData({
            ...nextData,
            isMailingAddressSame: false,
            isRegisteredAddressSame: false,
        });
    };

    const handleAddressListWrapperChange = (type, nextData) => {
        if (type === "REGISTERED") {
            handleUpdateFormData({ ...nextData, isRegisteredAddressSame: false });
            return;
        }
        if (type === "MAILING") {
            handleUpdateFormData({ ...nextData, isMailingAddressSame: false });
            return;
        }
        handleUpdateFormData(nextData);
    };

    const handleAddressSameChange = (type) => {
        if (type === "REGISTERED") {
            setData((prev) => {
                const newVal = !prev?.isRegisteredAddressSame;
                let newAddressList = prev?.addressList ? [...prev.addressList] : [];
                if (newVal && prev?.address) {
                    const idx = newAddressList.findIndex((item) => item.type === "REGISTERED");
                    const updatedAddress = buildMirroredAddressRow(prev.address, "REGISTERED", idx > -1 ? newAddressList[idx] : null);
                    if (idx > -1) {
                        newAddressList[idx] = updatedAddress;
                    } else {
                        newAddressList.push(updatedAddress);
                    }
                }
                return { ...prev, isRegisteredAddressSame: newVal, addressList: newAddressList };
            });
            return;
        }

        setData((prev) => {
            const newVal = !prev?.isMailingAddressSame;
            let newAddressList = prev?.addressList ? [...prev.addressList] : [];
            if (newVal && prev?.address) {
                const idx = newAddressList.findIndex((item) => item.type === "MAILING");
                const updatedAddress = buildMirroredAddressRow(prev.address, "MAILING", idx > -1 ? newAddressList[idx] : null);
                if (idx > -1) {
                    newAddressList[idx] = updatedAddress;
                } else {
                    newAddressList.push(updatedAddress);
                }
            }
            return { ...prev, isMailingAddressSame: newVal, addressList: newAddressList };
        });
    };

    const addEntity = () => {
        setData((prevItems) => ({
            ...prevItems,
            entities: [...(prevItems?.entities || []), { _isNew: true, _localId: buildLocalId() }],
            hasEntity: true,
        }));
    };

    const addUser = () => {
        setData((prevItems) => ({
            ...prevItems,
            individuals: [...(prevItems?.individuals || []), {
                isAuthorizedUser: true,
                accessType: "NoGroup",
                isPrimaryUser: false,
                isShareHolder: false,
                isControlPerson: false,
                isIndependentDirector: false,
                _isNew: true,
                _localId: buildLocalId(),
            }],
        }));
    };

    const addShareholder = () => {
        setData((prevItems) => ({
            ...prevItems,
            individuals: [...(prevItems?.individuals || []), {
                isPrimaryUser: false,
                isShareHolder: true,
                isAuthorizedUser: false,
                isControlPerson: false,
                isIndependentDirector: false,
                _isNew: true,
                _localId: buildLocalId(),
            }],
        }));
    };

    const addIndependentDirector = () => {
        setData((prevItems) => ({
            ...prevItems,
            individuals: [...(prevItems?.individuals || []), {
                isPrimaryUser: false,
                isShareHolder: false,
                isAuthorizedUser: false,
                isControlPerson: false,
                isIndependentDirector: true,
                _isNew: true,
                _localId: buildLocalId(),
            }],
        }));
    };

    const addControlPerson = () => {
        setData((prevItems) => ({
            ...prevItems,
            individuals: [...(prevItems?.individuals || []), {
                isPrimaryUser: false,
                isControlPerson: true,
                isAuthorizedUser: false,
                isShareHolder: false,
                isIndependentDirector: false,
                _isNew: true,
                _localId: buildLocalId(),
            }],
        }));
    };

    const removeEntity = (index) => {
        setData((prevItems) => {
            const newItems = [...(prevItems?.entities || [])];
            newItems.splice(index, 1);
            return {
                ...prevItems,
                entities: newItems,
                hasEntity: newItems.length > 0 ? prevItems?.hasEntity : false,
            };
        });
    };

    const removeUser = (index) => {
        setData((prevItems) => {
            const newItems = [...(prevItems?.individuals || [])];
            newItems.splice(index, 1);
            return {
                ...prevItems,
                individuals: newItems.map((item, itemIndex) => ({
                    ...item,
                    isPrimaryUser: itemIndex === 0,
                })),
            };
        });
    };

    return (
        <div data-testid="business-v2-business-information-plus-step">
            <p className='bold-text pt-2'>
                In order to apply for Plus account, we will need to review some prequalifications. Please answer the following questions:
            </p>

            <h1 className='pt-3'>General Business Information</h1>

            <Row>
                <WrapperField
                    disabled={asLabel}
                    jsonField={cyclosFields["Business_Name"]}
                    dataObj={data}
                    onChange={handleUpdateFormData}
                    showError={hasError}
                    onError={(errorMsg) => handleErrors('Business_Name', errorMsg)}
                />
            </Row>

            <Row>
                <WrapperField
                    disabled={asLabel}
                    jsonField={cyclosFields["Doing_Business_As"]}
                    dataObj={data}
                    onChange={handleUpdateFormData}
                    showError={hasError}
                    onError={(errorMsg) => handleErrors('Doing_Business_As', errorMsg)}
                />
            </Row>

            <InputLabel required label={"Address of Principal Place of Business (e.g. operating address)"} labelStyle={{ fontSize: 15 }} />
            <p className='ps-1 mb-0' style={{ fontSize: 11, fontStyle: 'italic' }}>
                This is the address you operate your business at, not a registered address. Please do not enter P.O. Boxes, they will not be accepted.
            </p>

            <Row>
                <WrapperField
                    disabled={asLabel}
                    jsonField={cyclosFields["address.street"]}
                    dataObj={data}
                    haveDependentSubFields
                    onChange={handlePrincipalAddressWrapperChange}
                    customProp={iPlusFields?.filter((field) => field?.parentField === "address")}
                    showError={hasError}
                    onError={(errorMsg, fieldPath) => handleErrors(fieldPath || 'address.street', errorMsg)}
                />
            </Row>

            <Row>
                <WrapperField
                    disabled={asLabel}
                    jsonField={compactAddressField(cyclosFields, "address.buildingNumber")}
                    dataObj={data}
                    onChange={handlePrincipalAddressWrapperChange}
                    showError={hasError}
                    onError={(errorMsg) => handleErrors('address.buildingNumber', errorMsg)}
                />
            </Row>

            <Row className='mb-3'>
                <WrapperField
                    className="col-sm-6 col-md-3"
                    disabled={asLabel}
                    jsonField={compactAddressField(cyclosFields, "address.city")}
                    dataObj={data}
                    onChange={handlePrincipalAddressWrapperChange}
                    showError={hasError}
                    onError={(errorMsg) => handleErrors('address.city', errorMsg)}
                />
                <WrapperField
                    className="col-sm-6 col-md-3"
                    disabled={asLabel}
                    jsonField={compactAddressField(cyclosFields, "address.state")}
                    dataObj={data}
                    onChange={handlePrincipalAddressWrapperChange}
                    showError={hasError}
                    onError={(errorMsg) => handleErrors('address.state', errorMsg)}
                />
                <WrapperField
                    className="col-sm-6 col-md-3"
                    disabled={asLabel}
                    jsonField={compactAddressField(cyclosFields, "address.pinCode")}
                    dataObj={data}
                    onChange={handlePrincipalAddressWrapperChange}
                    showError={hasError}
                    onError={(errorMsg) => handleErrors('address.pinCode', errorMsg)}
                />
                <WrapperField
                    className="col-sm-6 col-md-3 mt-3"
                    disabled={asLabel}
                    jsonField={compactAddressField(cyclosFields, "address.country")}
                    dataObj={data}
                    onChange={handlePrincipalAddressWrapperChange}
                    showError={hasError}
                    onError={(errorMsg) => handleErrors('address.country', errorMsg)}
                />
            </Row>

            {data?.addressList?.map((address, index) => {
                const checkboxId = `mailingCheckbox-${address?.type}-${index}`;
                const borderBottom = index === data?.addressList?.length - 1 ? { borderBottom: "1px solid #ddd" } : {};
                return (
                    <div style={borderBottom} className='address-box-tab position-relative' key={address?._localId || `${address?.type}-${index}`}>
                        <div className='d-flex align-items-center justify-content-between' style={{ margin: "0px 0px 5px 5px" }}>
                            <span style={{ fontSize: 16, fontWeight: "500", display: "block" }}>
                                {ADDRESS_LABELS[address?.type] ?? ""}
                                <font color="red">*</font>
                            </span>

                            <div className="mb-0 ms-1 d-flex align-items-center">
                                <Input
                                    type="checkbox"
                                    id={checkboxId}
                                    data-testid={checkboxId}
                                    checked={address?.type === "MAILING" ? data?.isMailingAddressSame : data?.isRegisteredAddressSame}
                                    onChange={() => handleAddressSameChange(address?.type)}
                                    disabled={asLabel}
                                />
                                <label htmlFor={checkboxId} className="ps-2 mt-2">
                                    Same as the principal place of business
                                </label>
                            </div>
                        </div>

                        <Row>
                            <WrapperField
                                disabled={asLabel}
                                jsonField={contextualStreetField(
                                    cyclosFields,
                                    "addressList[].street",
                                    ADDRESS_LABELS[address?.type] || "Address"
                                )}
                                arrayIndexing={index}
                                dataObj={data}
                                haveDependentSubFields
                                onChange={(nextData) => handleAddressListWrapperChange(address?.type, nextData)}
                                customProp={iPlusFields?.filter((field) => field?.parentField === "addressList[]")}
                                showError={hasError}
                                requiredOverride
                                onError={(errorMsg, fieldPath) => handleErrors(fieldPath || `addressList[${index}].street`, errorMsg)}
                            />
                        </Row>
                        <Row>
                            <WrapperField
                                disabled={asLabel}
                                jsonField={compactAddressField(cyclosFields, "addressList[].buildingNumber")}
                                arrayIndexing={index}
                                dataObj={data}
                                onChange={(nextData) => handleAddressListWrapperChange(address?.type, nextData)}
                                showError={hasError}
                                onError={(errorMsg) => handleErrors(`addressList[${index}].buildingNumber`, errorMsg)}
                            />
                        </Row>
                        <Row>
                            <WrapperField
                                className="col-sm-6 col-md-3"
                                disabled={asLabel}
                                jsonField={compactAddressField(cyclosFields, "addressList[].city")}
                                arrayIndexing={index}
                                dataObj={data}
                                onChange={(nextData) => handleAddressListWrapperChange(address?.type, nextData)}
                                showError={hasError}
                                onError={(errorMsg) => handleErrors(`addressList[${index}].city`, errorMsg)}
                            />
                            <WrapperField
                                className="col-sm-6 col-md-3"
                                disabled={asLabel}
                                jsonField={compactAddressField(cyclosFields, "addressList[].state")}
                                arrayIndexing={index}
                                dataObj={data}
                                onChange={(nextData) => handleAddressListWrapperChange(address?.type, nextData)}
                                showError={hasError}
                                onError={(errorMsg) => handleErrors(`addressList[${index}].state`, errorMsg)}
                            />
                            <WrapperField
                                className="col-sm-6 col-md-3"
                                disabled={asLabel}
                                jsonField={compactAddressField(cyclosFields, "addressList[].pinCode")}
                                arrayIndexing={index}
                                dataObj={data}
                                onChange={(nextData) => handleAddressListWrapperChange(address?.type, nextData)}
                                showError={hasError}
                                onError={(errorMsg) => handleErrors(`addressList[${index}].pinCode`, errorMsg)}
                            />
                            <WrapperField
                                className="col-sm-6 col-md-3 mt-3"
                                disabled={asLabel}
                                jsonField={compactAddressField(cyclosFields, "addressList[].country")}
                                arrayIndexing={index}
                                dataObj={data}
                                onChange={(nextData) => handleAddressListWrapperChange(address?.type, nextData)}
                                showError={hasError}
                                onError={(errorMsg) => handleErrors(`addressList[${index}].country`, errorMsg)}
                            />
                        </Row>
                    </div>
                );
            })}

            <WrapperField disabled={asLabel} jsonField={cyclosFields["Business_Registration_Country"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('Business_Registration_Country', errorMsg)} />
            <WrapperField disabled={asLabel} jsonField={cyclosFields["EIN"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('EIN', errorMsg)} />
            <WrapperField disabled={asLabel} jsonField={cyclosFields["Business_Tax_ID"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('Business_Tax_ID', errorMsg)} />
            <WrapperField disabled={asLabel} jsonField={cyclosFields["mobilePhone"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('mobilePhone', errorMsg)} customProp={iPlusFields?.find((field) => field?.field === "mobilePhone")} />
            <div className='mt-4'>
                <WrapperField disabled={asLabel} jsonField={cyclosFields["landlinePhone"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('landlinePhone', errorMsg)} />
            </div>

            <div className='ms-1'>
                <WrapperField disabled={asLabel} jsonField={cyclosFields["Entity_Type"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('Entity_Type', errorMsg)} />
            </div>

            <WrapperField className="col-sm-6" disabled={asLabel} jsonField={cyclosFields["Business_Registration_Number"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('Business_Registration_Number', errorMsg)} />
            <WrapperField className="col-sm-6" disabled={asLabel} jsonField={cyclosFields["Business_Formation_Date"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('Business_Formation_Date', errorMsg)} />
            <WrapperField disabled={asLabel} jsonField={cyclosFields["inOperation"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('inOperation', errorMsg)} />
            <WrapperField className="col-sm-6" disabled={asLabel} jsonField={cyclosFields["operationStartDate"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('operationStartDate', errorMsg)} />
            <WrapperField className="col-sm-6" disabled={asLabel} jsonField={cyclosFields["operateEstDate"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('operateEstDate', errorMsg)} />
            <WrapperField disabled={asLabel} jsonField={cyclosFields["Business_Industry_Type"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('Business_Industry_Type', errorMsg)} />
            <WrapperField disabled={asLabel} jsonField={cyclosFields["website"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('website', errorMsg)} />
            <WrapperField disabled={asLabel} jsonField={cyclosFields["url"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('url', errorMsg)} />
            <WrapperField disabled={asLabel} jsonField={cyclosFields["engage"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('engage', errorMsg)} />
            <WrapperField disabled={asLabel} jsonField={cyclosFields["empCount"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('empCount', errorMsg)} />
            <WrapperField disabled={asLabel} jsonField={cyclosFields["Bank_Account_Another_Financial_Institution"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('Bank_Account_Another_Financial_Institution', errorMsg)} />
            <WrapperField disabled={asLabel} jsonField={cyclosFields["additionalBankAccountInstName"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('additionalBankAccountInstName', errorMsg)} />
            <WrapperField disabled={asLabel} jsonField={cyclosFields["penalty"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('penalty', errorMsg)} />
            <WrapperField disabled={asLabel} jsonField={cyclosFields["penaltyDetails"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('penaltyDetails', errorMsg)} />
            <WrapperField disabled={asLabel} jsonField={cyclosFields["litigation"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('litigation', errorMsg)} />
            <WrapperField disabled={asLabel} jsonField={cyclosFields["litigationDetails"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('litigationDetails', errorMsg)} />
            <div>{<WrapperField disabled={asLabel} className="my-3" jsonField={cyclosFields["BUS_Source_Of_Funds"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('BUS_Source_Of_Funds', errorMsg)} />}</div>
            <div>
                <WrapperField disabled={asLabel} jsonField={cyclosFields["liquidFunds"]} dataObj={data} onChange={handleUpdateFormData} showError={hasError} onError={(errorMsg) => handleErrors('liquidFunds', errorMsg)} />
                <WrapperField
                    disabled={asLabel}
                    jsonField={cyclosFields["hasEntity"]}
                    dataObj={data}
                    onChange={(next) => {
                        if (next?.hasEntity === false) {
                            handleUpdateFormData({ ...next, entities: [] });
                            return;
                        }
                        if (next?.hasEntity === true && (!Array.isArray(next?.entities) || next.entities.length === 0)) {
                            handleUpdateFormData({ ...next, entities: [{ _isNew: true, _localId: buildLocalId() }] });
                            return;
                        }
                        handleUpdateFormData(next);
                    }}
                    showError={hasError}
                    onError={(errorMsg) => handleErrors('hasEntity', errorMsg)}
                />
            </div>

            {data?.hasEntity ? (data?.entities || [{}]).map((_, i) => (
                <div className='box-tab' key={data?.entities?.[i]?._localId || data?.entities?.[i]?._id || i}>
                    <div className='d-flex align-items-center justify-content-between'>
                        <div style={{ fontWeight: 'bold', fontSize: 18 }}>Entity {i + 1}</div>
                        <div className='me-2' onClick={() => removeEntity(i)}>
                            {data?.entities?.length > 1 && !asLabel && <FaTimes size={22} color='red' />}
                        </div>
                    </div>
                    <Entity
                        index={i}
                        asLabel={asLabel}
                        data={data}
                        handleChange={handleUpdateFormData}
                        hasError={hasError}
                        cyclosFields={cyclosFields}
                        setErrors={setErrors}
                        iPlusFields={iPlusFields}
                        isForEDD={false}
                        isEditable={!asLabel}
                    />
                </div>
            )) : null}
            {asLabel || !data?.hasEntity ? null : <BTN onClick={addEntity} label="Add Entity" />}

            <div>
                <h2>Officers or Authorized Account Users</h2>
                In this section, list all directors and officers in your organization. Officers include Chief Executive Officer, Chief Financial Officer, Chief Operating Officer, Director or other persons that exert control over the business. You may also list persons who are not controllers but whom you request have informational access to your account for operational purposes.
                <h2 className='mt-4'>Enter User information, click Add User below to add more users</h2>
            </div>

            {(data?.individuals || [{}]).map((_, i) => (
                <div className='box-tab' key={data?.individuals?.[i]?._localId || data?.individuals?.[i]?._id || i}>
                    <div className='d-flex align-items-center justify-content-between'>
                        <div style={{ fontWeight: 'bold', fontSize: 18 }}>
                            {data?.individuals?.[i]?.isPrimaryUser ? `Primary User` : `User ${i + 1}`}
                            {data?.individuals?.[i]?.isControlPerson ? <span className='ms-2' style={CONTROL_PERSON_BADGE_STYLE}>Control Prong</span> : null}
                        </div>
                        <div className='me-2' onClick={() => removeUser(i)}>
                            {data?.individuals?.length > 1 && !asLabel && i !== 0 && <FaTimes size={22} color='red' />}
                        </div>
                    </div>
                    <User
                        index={i}
                        asLabel={asLabel}
                        data={data}
                        handleChange={handleUpdateFormData}
                        hasError={hasError}
                        cyclosFields={cyclosFields}
                        setErrors={setErrors}
                        iPlusFields={iPlusFields}
                        isFieldEditable={() => true}
                        isForEDD={false}
                        applicationForm={applicationForm}
                        isEditable={!asLabel}
                    />
                </div>
            ))}

            {asLabel ? null : (
                <div className='d-flex flex-wrap gap-2'>
                    <BTN onClick={addUser} label="Add Officer/Authorized User" disabled={data?.individuals?.filter((ind) => ind?.isAuthorizedUser)?.length >= effectiveMaxAuthorizedUsers} />
                    <BTN onClick={addShareholder} label="Add Beneficial Owner/Shareholder" />
                    <BTN onClick={addControlPerson} label="Add Control Prong" />
                    <BTN onClick={addIndependentDirector} label="Add Director/Manager" />
                </div>
            )}

            {!hideButtons && (
                <div className='mt-4'>
                    <ButtonGroupNew
                        onNextClick={handleSubmit}
                        onBackClick={() => handlePrev?.()}
                        step={pageNum}
                        showPageNum
                        pageNum={pageNum}
                        asLabel={false}
                        loading={loading}
                        isDisabled={asLabel}
                    />
                </div>
            )}
        </div>
    );
};

export default observer(BusinessInformationPlus);
